import type { ReactNode } from 'react';
import { Box } from '@mui/material';
import { useDraggable, useDroppable } from '@dnd-kit/core';
import type { BlockField } from '@/blocks/domain/types';
import { canvasElementDragId } from '@/blocks/domain/helpers/canvasDragIds';
import { ElementInsetFrame } from '../blocks/fields/EditorInsetFrame';
import { SelectableElement } from './SelectableElement';
import { CanvasDragHandle } from './CanvasDragHandle';
import { CanvasInsertionLine } from './CanvasInsertionLine';
import { CanvasDropIndicator } from './CanvasDropIndicator';
import { useCanvasElementMenu } from './useCanvasElementMenu';
import { useCanvasStructure, useCanvasDragState, type CanvasSlot, type CanvasStructureApi } from './CanvasStructureContext';

export interface CanvasElementShellProps {
  field: BlockField;
  name: string;
  /** Where the element sits; absent on the legacy flat header (no insertion lines there). */
  slot?: CanvasSlot;
  children: ReactNode;
}

const frameTestId = (id: string) => `document-render:field:${id}`;
const selectableTestId = (id: string) => `document-render:selectable-element:${id}`;

/**
 * One element on the page. Read-only / quote canvas / export: today's frame + selectable box.
 * Block editor canvas (a `CanvasStructureProvider` is mounted): the element is also a drag source
 * (its tab handle) and a drop target, carries the ⋮ menu and inline tools, and gets a "+"
 * insertion line on its edges. Every addition is absolutely positioned - the page height the
 * measurement pass sees is unchanged.
 */
export function CanvasElementShell(props: CanvasElementShellProps) {
  const api = useCanvasStructure();
  if (!api) {
    return (
      <ElementInsetFrame testId={frameTestId(props.field.id)}>
        <SelectableElement elementId={props.field.id} name={props.name} testId={selectableTestId(props.field.id)}>
          {props.children}
        </SelectableElement>
      </ElementInsetFrame>
    );
  }
  return <StructuredElement {...props} api={api} />;
}

function StructuredElement({ field, name, slot, children, api }: CanvasElementShellProps & { api: CanvasStructureApi }) {
  const dragId = canvasElementDragId(field.id);
  const drag = useDraggable({ id: dragId, data: { name } });
  const drop = useDroppable({ id: dragId, data: { name } });
  const dragState = useCanvasDragState();
  const menuItems = useCanvasElementMenu(api, field, name);
  const showIndicator = dragState.overId === dragId && dragState.activeId !== dragId;

  const handle = (
    <CanvasDragHandle
      label={`Move ${name}`}
      setActivatorNodeRef={drag.setActivatorNodeRef}
      onPointerDown={drag.listeners?.onPointerDown as CanvasDragHandleListener}
      onMove={(delta) => api.moveElementBy(field.id, delta)}
      testId={`${selectableTestId(field.id)}:drag-handle`}
    />
  );

  return (
    <Box
      ref={(node: HTMLElement | null) => {
        drag.setNodeRef(node);
        drop.setNodeRef(node);
      }}
      data-canvas-element={field.id}
      sx={{
        position: 'relative',
        opacity: drag.isDragging ? 0.4 : 1,
        '&:hover > [data-insertion-line], &:focus-within > [data-insertion-line]': { opacity: 1 }
      }}
    >
      <ElementInsetFrame testId={frameTestId(field.id)}>
        <SelectableElement elementId={field.id} name={name} menuItems={menuItems} handle={handle} testId={selectableTestId(field.id)}>
          {children}
        </SelectableElement>
      </ElementInsetFrame>
      {showIndicator ? <CanvasDropIndicator edge={dragState.after ? 'bottom' : 'top'} /> : null}
      {slot && slot.index === 0 && !dragState.activeId ? (
        <CanvasInsertionLine
          slot={slot}
          edge="top"
          label={`Insert element before ${name}`}
          testId={`${frameTestId(field.id)}:insert-before`}
        />
      ) : null}
      {slot && !dragState.activeId ? (
        <CanvasInsertionLine
          slot={{ ...slot, index: slot.index + 1 }}
          edge="bottom"
          label={`Insert element after ${name}`}
          testId={`${frameTestId(field.id)}:insert-after`}
        />
      ) : null}
    </Box>
  );
}

type CanvasDragHandleListener = Parameters<typeof CanvasDragHandle>[0]['onPointerDown'];
